import { useCallback, useLayoutEffect, useState } from "react";

import { EntityFragment } from "@/__generated__/graphql";
import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import { Popper } from "@/components/atoms/Popper";
import { NodeList } from "@/components/organisms/Node/NodeList";
import { useDevice } from "@/hooks/useDevice";
import { useScope } from "@/hooks/useScope";
import { Objects } from "@/objects";
import { State } from "@/state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

export function AppTopbarEntityPickerPopper() {
  const {
    entityIdentifiers,
    activeEntityIdentifiers,
    activeEntity,
    isLoading,
  } = useScope();
  const activeEntityIdentifier = activeEntityIdentifiers?.[0];
  const [transientEntityIdentifiers, setTransientEntityIdentifiers] =
    useState(entityIdentifiers);

  useLayoutEffect(() => {
    setTransientEntityIdentifiers(entityIdentifiers);
  }, [entityIdentifiers]);

  const [isOpen, setIsOpen] = useState(false);
  // On a phone - or in a window as narrow, with no sidebar - it leads the
  // bar, where a desktop's sidebar has the business's box and its name: led
  // by that box, as it is there - and drawn as a desktop's picker, how many
  // of the businesses are in view in its count.
  const { isNarrow } = useDevice();

  const getIsChecked = useCallback(
    (item: EntityFragment) =>
      activeEntityIdentifiers?.includes(item.identifier) || false,
    [activeEntityIdentifiers],
  );

  return (
    <Popper
      isOpenState={[
        isOpen,
        (isOpen) => {
          setIsOpen(isOpen);
          setTransientEntityIdentifiers(entityIdentifiers);
        },
      ]}
      placement={isNarrow ? "bottom-start" : "bottom-end"}
      anchor={
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "pr-1",
                isNarrow ? "pl-1" : "pl-2",
                isNarrow ? "max-w-65" : "max-w-50",
              )}
            >
              {isNarrow ? (
                <BoxForEntity
                  identifier={activeEntityIdentifier}
                  representation="character"
                  size="sm"
                />
              ) : undefined}
              <Typography level="body-md" className="truncate min-w-0">
                <Skeleton loading={isLoading}>
                  {isLoading && "............................"}
                  {!isLoading && (activeEntity?.name || activeEntityIdentifier)}
                </Skeleton>
              </Typography>
              {!isLoading && (
                <Typography
                  level="body-sm"
                  className={cn(
                    "text-muted-foreground",
                    activeEntityIdentifiers?.length > 1 ? "ml-1" : "ml-0",
                  )}
                >
                  {activeEntityIdentifiers?.length - 1 &&
                  activeEntityIdentifiers?.length > 1
                    ? `+${activeEntityIdentifiers?.length - 1}`
                    : ""}
                </Typography>
              )}
              <div className="[border-width:1px] [border-style:solid] border-border bg-muted rounded flex items-center justify-center pl-1 pr-1 pt-0 pb-0 ml-2 min-h-5">
                <Typography level="body-sm" className="text-muted-foreground">
                  <Skeleton loading={isLoading}>
                    {isLoading && "....."}
                    {!isLoading &&
                      `${activeEntityIdentifiers?.length}/${entityIdentifiers?.length}`}
                  </Skeleton>
                </Typography>
              </div>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{`
              You are currently viewing ${activeEntityIdentifiers?.length}/${entityIdentifiers?.length} entities
            `}</TooltipContent>
        </Tooltip>
      }
      floatingStyle={{ width: "min(400px, calc(100vw - 16px))" }}
    >
      <NodeList
        key={activeEntityIdentifiers?.join(",") || "empty"}
        {...Objects?.EntityObject?.getReferenceValueOptions()}
        size="md"
        isMultiselect={true}
        isCheckboxesEnabled={false}
        isNullable={false}
        getIsChecked={getIsChecked}
        onSelectMultiSelectOption={(item: EntityFragment) => {
          // Prevent deselection if this is the only selected item
          if (transientEntityIdentifiers?.length === 1) {
            return; // Do nothing, cannot deselect the last item
          }

          // restack entity identifiers with the selected entity at the top
          setTransientEntityIdentifiers([
            item.identifier,
            ...transientEntityIdentifiers.filter(
              (identifier) => identifier !== item.identifier,
            ),
          ]);
        }}
        onSubmitCheckedRows={(checkedRows: EntityFragment[]) => {
          setIsOpen(false);

          // set global entity identifiers, ensuring the selected row is at the top
          State.setState({
            activeEntityIdentifiers: [
              ...checkedRows.map((row) => row.identifier),
            ],
          });
        }}
      />
    </Popper>
  );
}
